import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle, Sparkles, Lightbulb } from "lucide-react";
import PageBreadcrumb, { BreadcrumbCrumb } from "@/components/PageBreadcrumb";
import { usePageContent } from "@/hooks/usePageContent";
import { FAQS_SEED, type FaqItem, type FaqPage } from "@/data/faqs";
import { LOCAL_AREAS_SEED, type LocalAreaItem } from "@/data/localAreas";

const getDestinationType = (to: string): string => {
  const path = to.split("#")[0].replace(/\/$/, "");
  const hash = to.split("#")[1];
  switch (path) {
    case "/beaches": return "Beach";
    case "/dining": return "Restaurant";
    case "/nightlife": return "Venue";
    case "/events": return "Event";
    case "/trails": return "Trail";
    case "/activities": return "Activity";
    case "/surf": return "Surf spot";
    case "/news": return "Article";
    case "/weather": return "Forecast";
    case "/tides": return hash === "moon-phase" ? "Moon phase" : "Tide window";
    case "/map": return "Map";
    default: return "Page";
  }
};

export interface ArticleFaq {
  q: string;
  a: string;
}

export interface ArticleBestFor {
  label: string;
  description: string;
  to: string;
  /** Name of the top pick — shown as a chip on the card. */
  pickName?: string;
  /** Optional runner-up suggestion. */
  runnerUp?: { label: string; to: string };
  /** Optional link to a related topical page. */
  related?: { label: string; to: string };
}

export interface ArticleTip {
  title: string;
  body: string;
}

interface ArticleShellProps {
  eyebrow: string;
  title: string;
  /** Italicised tail of the title — appears on its own line, e.g. "Coastline" */
  titleAccent?: string;
  /** Use H2 when a live tool above this article already owns the page H1. */
  headingLevel?: 1 | 2;
  /**
   * Literal heading element supplied by the route, e.g. <h1>San Diego Coastal Map</h1>.
   * Rendered in place of the generated title heading so the route file itself
   * declares the page's main heading.
   */
  titleHeading?: ReactNode;
  intro: ReactNode;
  /** Existing section component (the card grid). */
  section: ReactNode;
  faqs: ArticleFaq[];
  bestFor?: ArticleBestFor[];
  tips: ArticleTip[];
  /** Breadcrumb trail (Home is prepended automatically). */
  breadcrumb?: BreadcrumbCrumb[];
  /** JSON-LD FAQPage schema is generated automatically from `faqs`. */
  faqsJsonLd?: boolean;
  /** Page key used to fetch DB-managed FAQs and "Find your perfect match" cards. */
  page?: FaqPage;
  /** Optional H2 overrides per section. ReactNode so callers can include <span className="italic"> accents. */
  bestForHeading?: ReactNode;
  tipsHeading?: ReactNode;
  faqHeading?: ReactNode;
}

const ArticleShell = ({
  eyebrow,
  title,
  titleAccent,
  headingLevel = 1,
  titleHeading,
  intro,
  section,
  faqs,
  bestFor,
  tips,
  breadcrumb,
  page,
  bestForHeading,
  tipsHeading,
  faqHeading,
}: ArticleShellProps) => {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  // Pull DB-managed FAQs / Local Areas for this page (falls back to local seed).
  const { items: dbFaqs } = usePageContent<FaqItem>("faqs", page ?? "beaches", FAQS_SEED);
  const { items: dbAreas } = usePageContent<LocalAreaItem>("local_areas", page ?? "beaches", LOCAL_AREAS_SEED);

  const effectiveFaqs: ArticleFaq[] = page
    ? dbFaqs.map((f) => ({ q: f.question, a: f.answer }))
    : faqs;
  const effectiveBestFor: ArticleBestFor[] | undefined = page
    ? dbAreas.map((a) => ({
        label: a.label,
        description: a.description,
        to: a.to,
        pickName: a.pickName,
        runnerUp: a.runnerUp,
        related: a.related,
      }))
    : bestFor;

  // Default breadcrumb: Home > Guides > <title>. Callers may override with a
  // custom trail (e.g. a beach or category article).
  const effectiveBreadcrumb: BreadcrumbCrumb[] =
    breadcrumb && breadcrumb.length > 0
      ? breadcrumb
      : [{ label: "Guides", to: "/guides" }, { label: title }];

  return (
  <article>
    {/* Hero / intro */}
    <section className="bg-gradient-to-b from-secondary/40 via-background to-background px-6 pt-16 pb-10">
      <div className="mx-auto max-w-4xl">
        <div className="mb-5">
          <PageBreadcrumb items={effectiveBreadcrumb} />
        </div>
        <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">
          {eyebrow}
        </p>
        {titleHeading ?? (
          <Heading className="font-display mt-3 text-4xl font-semibold leading-tight text-foreground sm:text-5xl md:text-6xl">
            {title}
            {titleAccent && (
              <>
                {" "}
                <span className="italic">{titleAccent}</span>
              </>
            )}
          </Heading>
        )}
        <div className="font-body mt-6 space-y-4 text-base leading-relaxed text-foreground/80 sm:text-lg">
          {intro}
        </div>
      </div>
    </section>

    {/* Existing section (card grid, etc.) */}
    {section}

    {/* Best for… */}
    {effectiveBestFor && effectiveBestFor.length > 0 && (
      <section className="bg-background px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="h-4 w-4" />
            <p className="font-body text-xs font-medium uppercase tracking-[0.3em]">
              Best for
            </p>
          </div>
          <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">
            {bestForHeading ?? (<>Find your <span className="italic">perfect match</span></>)}
          </h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {effectiveBestFor.map((item) => {
              const destType = getDestinationType(item.to);
              return (
              <div
                key={item.label}
                className="relative flex flex-col rounded-2xl border border-border bg-card p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-display text-lg font-semibold text-foreground">
                      {item.label}
                    </h3>
                    {item.pickName && (
                      <span className="font-body mt-2 inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                        Top pick: {item.pickName}
                      </span>
                    )}
                    <p className="font-body mt-2 text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className="font-body rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {destType}
                    </span>
                  </div>
                </div>
                {(item.runnerUp || item.related) && (
                  <div className="font-body mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
                    {item.runnerUp && (
                      <Link
                        to={item.runnerUp.to}
                        className="relative z-10 hover:text-primary"
                      >
                        Also great: <span className="font-medium">{item.runnerUp.label}</span>
                      </Link>
                    )}
                    {item.related && (
                      <Link
                        to={item.related.to}
                        className="relative z-10 inline-flex items-center gap-1 hover:text-primary"
                      >
                        {item.related.label} <ArrowRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </section>
    )}

    {/* Know before you go */}
    {tips.length > 0 && (
      <section className="bg-card px-6 py-20 border-y border-border">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center gap-2 text-primary">
            <Lightbulb className="h-4 w-4" />
            <p className="font-body text-xs font-medium uppercase tracking-[0.3em]">
              Know before you go
            </p>
          </div>
          <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">
            {tipsHeading ?? (<>Plan it <span className="italic">like a local</span></>)}
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {tips.map((tip) => (
              <div
                key={tip.title}
                className="rounded-2xl border border-border bg-background p-6"
              >
                <h3 className="font-display text-base font-semibold text-foreground">
                  {tip.title}
                </h3>
                <p className="font-body mt-2 text-sm leading-relaxed text-muted-foreground">
                  {tip.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    )}

    {/* FAQ */}
    {effectiveFaqs.length > 0 && (
      <section className="bg-background px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-2 text-primary">
            <HelpCircle className="h-4 w-4" />
            <p className="font-body text-xs font-medium uppercase tracking-[0.3em]">
              FAQ
            </p>
          </div>
          <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">
            {faqHeading ?? (<>Frequently <span className="italic">asked</span></>)}
          </h2>
          <dl className="mt-10 space-y-6">
            {effectiveFaqs.map((faq) => (
              <div
                key={faq.q}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <dt className="font-display text-lg font-semibold text-foreground">
                  {faq.q}
                </dt>
                <dd className="font-body mt-2 text-sm leading-relaxed text-muted-foreground">
                  {faq.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    )}
  </article>
  );
};

export const buildFaqJsonLd = (faqs: ArticleFaq[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

interface ArticleJsonLdInput {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
}

const SITE_URL = "https://sandiegocoastalguide.com";
const PUBLISHER = {
  "@type": "Organization",
  name: "San Diego Coastal Guide",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/favicon.ico`,
  },
};

export const buildArticleJsonLd = ({ headline, description, path, datePublished, dateModified, image }: ArticleJsonLdInput) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
  datePublished,
  dateModified: dateModified ?? datePublished,
  author: PUBLISHER,
  publisher: PUBLISHER,
  ...(image ? { image } : {}),
});

export default ArticleShell;
